import React from 'react';
import { Zap, Check, ArrowRight, Building2, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const PLANS = {
  basic: {
    name: 'Basic',
    price: 29,
    projects: 2,
    features: ['2 Projects', '1 Team Member', '10GB Storage', 'Email Support']
  },
  pro: {
    name: 'Pro',
    price: 69,
    projects: 5,
    features: ['5 Projects', '3 Team Members', '50GB Storage', 'AI Features', 'Priority Support'],
    recommended: true
  },
  enterprise: {
    name: 'Enterprise',
    price: 199,
    projects: -1,
    features: ['Unlimited Projects', 'Unlimited Team', '200GB Storage', 'White Label', 'Dedicated Support'],
    contact: true
  }
};

const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  currentTier = 'basic',
  currentProjects = 0,
  maxProjects = 2,
  onSelectPlan,
  onContactEnterprise
}) => {
  const navigate = useNavigate();
  
  const upgradePlans = Object.entries(PLANS).filter(([key]) => {
    if (currentTier === 'basic') return key === 'pro' || key === 'enterprise';
    if (currentTier === 'pro') return key === 'enterprise';
    return false;
  });

  const handleSelectPlan = (planId) => {
    if (planId === 'enterprise') {
      if (onContactEnterprise) {
        onContactEnterprise();
      } else {
        navigate('/pricing?plan=enterprise');
      }
    } else {
      if (onSelectPlan) {
        onSelectPlan(planId);
      } else {
        navigate(`/pricing?plan=${planId}`);
      }
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Upgrade to Add More Projects
          </DialogTitle>
          <DialogDescription>
            You've reached the project limit for your {PLANS[currentTier]?.name || 'current'} plan ({currentProjects}/{maxProjects} projects).
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {upgradePlans.map(([planId, plan]) => (
            <div 
              key={planId}
              className={`p-4 border rounded-xl transition-all ${
                plan.recommended 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {plan.recommended && (
                <div className="text-xs font-semibold text-blue-600 mb-2">RECOMMENDED</div>
              )}
              
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-bold">${plan.price}</span>
                    <span className="text-slate-500">/mo</span>
                  </div>
                  <ul className="mt-3 space-y-1">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  onClick={() => handleSelectPlan(planId)}
                  className={plan.contact ? 'bg-slate-800 hover:bg-slate-900' : 'bg-blue-600 hover:bg-blue-700'}
                >
                  {plan.contact ? (
                    <>
                      <Phone className="w-4 h-4 mr-2" />
                      Contact Us
                    </>
                  ) : (
                    <>
                      Upgrade
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 text-center">
          <Button variant="ghost" onClick={onClose}>
            Maybe Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
